import { useState } from 'react';
import { api } from '../api';
import * as fmt from '../format';
import type { Store } from '../store';
import { Dialog } from './Dialog';

/// A point picked somewhere other than here - off the map - to open the
/// editor on rather than the existing checkpoint or a round number.
export interface CheckpointSeed {
  miles: number;
  label?: string;
}

/// Set, move or clear the checkpoint. One editor, opened from wherever the
/// checkpoint appears.
export function CheckpointDialog({ store, seed, onClose }: {
  store: Store;
  seed?: CheckpointSeed;
  onClose: () => void;
}) {
  const existing = store.walk?.targetTracking ?? null;

  // A starting value, not a derived figure - just somewhere sensible for the
  // cursor to land.
  const total = store.summary?.totalActualMiles ?? 0;
  const nextHundred = Math.floor(total / 100) * 100 + 100;
  const suggestion = nextHundred < (store.walk?.distanceMiles ?? Infinity) ? nextHundred : null;

  // A seeded point wins: it is a deliberate choice made a moment ago, where
  // the existing checkpoint and the round number are only defaults. It is
  // rounded because a mileage picked off a map arrives as a long decimal, and
  // hundredths are already finer than anyone picked.
  const [miles, setMiles] = useState(
    seed ? seed.miles.toFixed(2) : String(existing?.miles ?? suggestion ?? ''),
  );
  const [label, setLabel] = useState(seed?.label ?? existing?.label ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entered = Number(miles);
  const valid = miles !== '' && Number.isFinite(entered) && entered > 0;

  async function run(work: () => Promise<unknown>) {
    if (!store.walk) return;
    setSaving(true);
    setError(null);
    try {
      await work();
      await store.reload();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog title={existing ? 'Move the checkpoint' : 'Set a checkpoint'} onClose={onClose}>
      <p className="muted">
        A distance worth chasing — a state line, or the next round number. Progress towards it
        shows on Today.
      </p>

      <label>
        At (miles)
        <input type="number" inputMode="decimal" min="0" value={miles}
               onChange={(e) => setMiles(e.target.value)} />
      </label>
      <label>
        Label
        <input value={label} placeholder="Kansas state line"
               onChange={(e) => setLabel(e.target.value)} />
      </label>

      {suggestion !== null && String(suggestion) !== miles && (
        <button className="link" onClick={() => setMiles(String(suggestion))}>
          Next round hundred — {fmt.miles(suggestion, 0)}
        </button>
      )}

      {error && <p className="error">{error}</p>}

      <div className="dialog__actions">
        {existing && (
          <button className="destructive" disabled={saving}
                  onClick={() => void run(() => api.clearTargetTracking(store.walk!.walkId))}>
            Clear
          </button>
        )}
        <button onClick={onClose}>Cancel</button>
        <button className="primary" disabled={!valid || saving}
                onClick={() => void run(() =>
                  api.setTargetTracking(store.walk!.walkId, {
                    miles: entered,
                    label: label === '' ? null : label,
                  }))}>
          Save
        </button>
      </div>
    </Dialog>
  );
}
